/**
 * Studio Zuiver: tattoo gallery (artist page "Tattoo artist work" and the shared "Tattoo portfolio").
 * Opens the photos in PhotoSwipe (Helix ships assets/photoswipe.js + photoswipe.css for the product
 * gallery). The library is only loaded when the visitor gets close to the gallery (pointer, focus or
 * touch), and a click before it is ready waits for it instead of navigating to the image URL.
 *
 * Tiles with the `hidden` attribute (filtered out or behind "show more") are skipped by the masonry
 * layout and the lightbox; call `refresh()` after changing them. Tiles with `data-caption` get a
 * caption bar in the lightbox when the gallery has `data-caption-ui` (HTML, e.g. a link to the artist).
 */
class TattooGallery extends HTMLElement {
  #lightbox = null;
  #loading = null;
  #armed = false;
  #resizeObserver = null;

  connectedCallback() {
    if (this.classList.contains('tattoo-artist-gallery__grid--masonry')) this.#initMasonry();
    if (!this.hasAttribute('data-lightbox')) return;
    this.addEventListener('pointerenter', this.#preload, { once: true });
    this.addEventListener('focusin', this.#preload, { once: true });
    this.addEventListener('touchstart', this.#preload, { once: true, passive: true });
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#destroyLightbox();
  }

  /** The tiles that take part in the layout and the lightbox (not hidden). */
  items() {
    return Array.from(this.querySelectorAll('a:not([hidden])'));
  }

  /** Re-run the masonry layout and rebuild the lightbox after tiles were shown or hidden. */
  refresh() {
    if (this.classList.contains('is-masonry')) this.#layoutMasonry();
    if (this.#armed) {
      this.#destroyLightbox();
      this.#init();
    }
  }

  #destroyLightbox() {
    if (this.#lightbox) {
      if (this.#lightbox.pswp && !this.#lightbox.pswp.isClosed) this.#lightbox.pswp.close();
      this.#lightbox.destroy?.();
      this.#lightbox = null;
    }
    this.#armed = false;
  }

  /**
   * Masonry: a CSS grid with 4px rows; every tile spans as many rows as its height needs.
   * The height follows from the column width and the photo ratio (`--item-ratio`, set in Liquid),
   * so the layout is right before the images have loaded. Re-run on resize.
   */
  #initMasonry() {
    this.classList.add('is-masonry');
    this.#layoutMasonry();
    this.#resizeObserver = new ResizeObserver(() => this.#layoutMasonry());
    this.#resizeObserver.observe(this);
  }

  #layoutMasonry() {
    const styles = getComputedStyle(this);
    const rowUnit = parseFloat(styles.gridAutoRows) || 4;
    const rowGap = parseFloat(styles.rowGap) || 0;
    const tiles = this.querySelectorAll('a:not(.tattoo-artist-gallery__item--extra):not([hidden])');
    const first = tiles[0];
    if (!first) return;
    const colWidth = first.getBoundingClientRect().width;
    if (!colWidth) return;
    tiles.forEach((tile) => {
      const ratio = parseFloat(tile.style.getPropertyValue('--item-ratio')) || 1;
      const height = colWidth / ratio;
      const span = Math.max(1, Math.round((height + rowGap) / (rowUnit + rowGap)));
      tile.style.gridRowEnd = `span ${span}`;
    });
  }

  #preload = () => {
    this.#load().catch(() => {});
  };

  /** Loads photoswipe.css/js once and builds the lightbox. */
  #load() {
    if (this.#loading) return this.#loading;
    const css = this.dataset.photoswipeCss;
    if (css && !document.querySelector(`link[href="${css}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = css;
      document.head.appendChild(link);
    }
    const ready = window.LuTheme?.PhotoSwipeLightbox
      ? Promise.resolve()
      : import(this.dataset.photoswipeSrc);
    this.#loading = ready.then(() => this.#init());
    return this.#loading;
  }

  #init() {
    if (this.#armed || !window.LuTheme?.PhotoSwipeLightbox) return;
    this.#armed = true;
    const arrow = (d) =>
      `<svg class="pswp__icn" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="${d}" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

    this.#lightbox = new window.LuTheme.PhotoSwipeLightbox({
      gallery: this,
      children: 'a:not([hidden])',
      pswpModule: window.LuTheme.PhotoSwipe,
      mainClass: this.hasAttribute('data-caption-ui') ? 'pswp--tattoo pswp--tattoo-caption' : 'pswp--tattoo',
      bgOpacity: 0.96,
      padding: { top: 48, bottom: this.hasAttribute('data-caption-ui') ? 88 : 48, left: 16, right: 16 },
      counter: true,
      zoom: false,
      // PhotoSwipe caps "fit" at the photo's natural size and only allows zooming when the secondary
      // level differs from it, so a photo that already fits at 1:1 (the current 500px uploads) could
      // not be zoomed at all. Zoom always goes to 2x the fitted size (up to 4x with pinch); with
      // full-size uploads that is real detail, with small uploads it is an upscale.
      secondaryZoomLevel: (levels) => levels.fit * 2,
      maxZoomLevel: (levels) => levels.fit * 4,
      imageClickAction: 'zoom',
      tapAction: 'toggle-controls',
      doubleTapAction: 'zoom',
      showHideAnimationType: 'zoom',
      showAnimationDuration: 300,
      hideAnimationDuration: 250,
      easing: 'cubic-bezier(0.4, 0, 0.22, 1)',
      closeOnVerticalDrag: true,
      arrowPrevSVG: arrow('m12 19-7-7 7-7M19 12H5'),
      arrowNextSVG: arrow('M5 12h14m-7-7 7 7-7 7'),
      closeSVG: arrow('M18 6 6 18M6 6l12 12'),
      zoomSVG: arrow('M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm10 2-4.3-4.3M11 8v6m-3-3h6'),
    });
    // The thumbnail is the <img> inside the anchor, so the zoom animation starts from the tile.
    this.#lightbox.addFilter('thumbEl', (thumbEl, data, index) => {
      const item = this.items()[index];
      return item?.querySelector('img') || thumbEl;
    });
    this.#lightbox.addFilter('placeholderSrc', (src, content) => {
      const item = this.items()[content.index];
      return item?.querySelector('img')?.currentSrc || src;
    });
    // Caption bar (portfolio): "Tattoo door <a>Leonie</a>", taken from the tile's data-caption.
    if (this.hasAttribute('data-caption-ui')) {
      this.#lightbox.on('uiRegister', () => {
        const pswp = this.#lightbox.pswp;
        pswp.ui.registerElement({
          name: 'tattoo-caption',
          className: 'pswp__tattoo-caption',
          appendTo: 'root',
          onInit: (el) => {
            const update = () => {
              const item = pswp.currSlide?.data?.element;
              const caption = item?.dataset.caption || '';
              el.innerHTML = caption;
              el.hidden = !caption;
            };
            pswp.on('change', update);
            update();
          },
        });
      });
    }
    this.#lightbox.init();
  }

  /** Before PhotoSwipe is ready a click must not navigate; open the viewer as soon as it is. */
  #onClick = (event) => {
    const link = event.target.closest('a');
    if (!link || !this.contains(link) || this.#armed) return;
    event.preventDefault();
    const index = this.items().indexOf(link);
    this.#load()
      .then(() => this.#lightbox?.loadAndOpen(index))
      .catch(() => window.open(link.href, '_blank', 'noopener'));
  };
}

if (!customElements.get('tattoo-gallery')) {
  customElements.define('tattoo-gallery', TattooGallery);
}
